import React, { useState, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useTheme } from '../../theme/ThemeContext';
import AppShell from '../../components/layout/AppShell';
import ScreenCommandHeader from '../../components/layout/ScreenCommandHeader';
import FloatingActionButton from '../../components/common/FloatingActionButton';
import DashboardTab from './DashboardTab';
import FeedTab, { FeedTabRef } from './FeedTab';
import { HomeStackParamList } from '../../navigation/HomeNavigator';

type HomeView = 'dashboard' | 'feed';
type HomeNavigationProp = StackNavigationProp<HomeStackParamList>;

const HomeScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<HomeNavigationProp>();
  const [selectedView, setSelectedView] = useState<HomeView>('dashboard');
  const feedTabRef = useRef<FeedTabRef>(null);

  // Refresh feed when screen comes into focus (after creating a post)
  useFocusEffect(
    React.useCallback(() => {
      if (selectedView === 'feed') {
        console.log('[HomeScreen] Screen focused, refreshing feed...');
        feedTabRef.current?.refresh();
      }
    }, [selectedView])
  );

  const handleCreatePost = () => {
    navigation.navigate('CreatePost', {});
  };

  const renderHeader = () => (
    <ScreenCommandHeader
      segments={[
        { value: 'dashboard' as const, label: 'Dashboard' },
        { value: 'feed' as const, label: 'Feed' },
      ]}
      selectedValue={selectedView}
      onValueChange={setSelectedView}
    />
  );

  return (
    <AppShell title="Home" showLogo={true} scrollable={false} showTopBar={false}>
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        {selectedView === 'dashboard' ? (
          <View style={styles.content}>
            <DashboardTab headerComponent={renderHeader()} showTopBar={true} />
          </View>
        ) : (
          <View style={styles.content}>
            <FeedTab ref={feedTabRef} headerComponent={renderHeader()} showTopBar={true} />
          </View>
        )}
        {selectedView === 'feed' && (
          <FloatingActionButton onPress={handleCreatePost} />
        )}
      </View>
    </AppShell>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
});

export default HomeScreen;
